import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useFirebase } from "react-redux-firebase";
import moment from "moment";

import { SettingsLeft, SettingsRight } from "./components/Settings";
import FadeTransition from "./Transitions/FadeTransition";
import * as socketActions from "./actions/socketActions";
import * as cuesApi from "./api/cue";
import * as mainActions from "./actions/mainActions";

import H2RButton from "./components/H2RComponents/H2RButton";
import H2RHeading from "./components/H2RComponents/H2RHeading";
import logo from "./components/shared/img/logo.png";
import H2RSelect from "./components/H2RComponents/H2RSelect";
import H2RInput from "./components/H2RComponents/H2RInput";
import ProBadge from "./components/shared/snippets/ProBadge";
import useIsPro from "./components/shared/hooks/useIsPro";
import compareVersion from "./components/shared/helpers/compare-versions";
import DisconnectedModal from "./components/DisconnectedModal";
import useIsElectron from "./components/shared/hooks/useIsElectron";
import OpenExternal from "./components/shared/helpers/OpenExternal";
import ProjectName from "./components/shared/ProjectName";
import { faCheck, faCog, faTv, faDownload, faExternalLink, faUpload, faClose, faPlus, faCopy, faTrash, faArrowLeft, faEllipsis } from "@fortawesome/free-solid-svg-icons";
import OBSLogo from './components/shared/img/obs-icon.png'
import { Tooltip, TooltipContent, TooltipTrigger } from "./components/shared/snippets/Tooltip";
import AddDataSource from "./components/shared/AddDataSource";

function updateOnlineStatus() {
	console.log('Online:', navigator.onLine);
}

window.addEventListener("online", updateOnlineStatus);
window.addEventListener("offline", updateOnlineStatus);

updateOnlineStatus();

const controls = [
	{
		name: "Control room",
		value: "control",
		url: "control",
	},
	{
		name: "Rundown",
		value: "rundown",
		url: "rundown",
	},
	{
		name: "Buttons",
		value: "buttons",
		url: "buttons",
	},
	{
		name: "Social",
		value: "social-only",
		url: "social-only",
	},
];

const outputs = [
	{
		name: "Preview",
		value: "preview",
		url: "preview",
		alternateOutput: false,
	},
	{
		name: "key",
		value: "output_key",
		url: "output",
		urlQuery: "?bg=%23000&key=true&muted=true",
		alternateOutput: false,
	},
	{
		name: "fill",
		value: "output_fill",
		url: "output",
		urlQuery: "?bg=%23000",
		alternateOutput: false,
	},
	{
		name: "1",
		value: "output_1",
		url: "output",
		alternateOutput: false,
	},

	{
		name: "2",
		value: "output_2",
		url: "output",
		alternateOutput: true,
	},
	{
		name: "3",
		value: "output_3",
		url: "output",
		alternateOutput: true,
	},
	{
		name: "4",
		value: "output_4",
		url: "output",
		alternateOutput: true,
	},
	{
		name: "utility",
		value: "output_utility",
		url: "output",
		urlQuery: "?bg=%23212121",
		alternateOutput: true,
	},
];

function App() {
	const [updateAvailable, setUpdateAvailable] = useState(false);
	const [showSettings, setShowSettings] = useState(false);
	const [controlTab, setControlTab] = useState(controls[1]);
	const [outputTab, setOutputTab] = useState(outputs[3]);
	const [addProjectFromCloud, setAddProjectFromCloud] = useState(false);
	const [addProjectFromCloudID, setAddProjectFromCloudID] = useState("");
	const [reallyDelete, setReallyDelete] = useState(false);
	const [openOnDisplayMenu, setOpenOnDisplayMenu] = useState(false);
	const [moreProjectOptionsMenu, setMoreProjectOptionsMenu] = useState(false);
	const [copied, setCopied] = useState(null)

	const store = useSelector((state) => state.main);
	const selectedProject = useSelector(
		(state) => state.main?.settings?.selectedProject
	);
	const firebase = useFirebase();
	const isPro = useIsPro();
	const syncing = useSelector((state) => state.main.syncing) || false;
	const availableDisplays =
		useSelector((state) => state.main.availableDisplays) || [];

	const selectedIpAddress = useSelector(
		(state) => state.main?.settings?.app_ip_address
	);

	const dataSources = useSelector((state) => state.main?.dataSources) || {};
	const isElectron = useIsElectron();

	const getSelectedIpAddress = () => {
		let ip = selectedIpAddress;
		let indx = 0;

		store.ip_addresses?.map((str, i) => {
			if (str.includes(ip)) {
				return (indx = i);
			} else return null;
		});

		return store.ip_addresses[indx];
	};

	const controlUrl = `${`http://${getSelectedIpAddress()}:${
											window.location.port
										}/`}${controlTab.url}/${selectedProject}/`

	const outputUrl = `${`http://${getSelectedIpAddress()}:${
											window.location.port
										}/`}${outputTab.url}/${store.settings?.selectedProject}/${
											outputTab.alternateOutput
												? `${outputTab.name}/${outputTab.urlQuery ?? ""}`
												: `${outputTab.urlQuery ?? ""}`
										}`

	const checkForUpdate = async () => {
		firebase
			.firestore()
			.doc("/versions/h2r-graphics")
			.get()
			.then((res) => {
				if (res.exists) {
					const current = store.version;
					const latest = res.data().current;

					if (compareVersion(latest, current) > 0) {
						setUpdateAvailable(true);
					}
				}
			})
			.catch((err) => {
				console.error(err);
			});
	};

	useEffect(() => {
		if (store.version !== undefined) {
			checkForUpdate();
		}
	}, [store.version]);

	useEffect(() => {
		console.log(store);
	}, [store]);


	useEffect(()=>{
		document.body.style.backgroundColor = 'black'
	}, [])

	useEffect(() => {
		if (openOnDisplayMenu) {
			socketActions.getAvailableDisplays();
		}
	}, [openOnDisplayMenu]);

	return (
		<div className="flex">
			<DisconnectedModal open={!store.socket_connected} />
			{/* LEFT */}
			<div className="bg-gray-900 min-h-screen w-3/12 min-w-min">
				<div style={{ WebkitAppRegion: "drag" }} className=""></div>

				<section
					className="px-6 py-8 h-screen flex flex-col justify-between items-center text-center"
					// style={{ height: `calc(100% - 2rem)` }}
				>
					{!showSettings ? (
						<div className="flex flex-col justify-center items-center space-y-4">
							<div className="">
								<img className="w-40" src={logo} alt="H2R Graphics Logo" />
							</div>

							<h5 className="border-2 rounded px-1 text-sm border-gray-700 text-gray-400 font-semibold">v{store.version}</h5>

							<FadeTransition show={updateAvailable}>
								<H2RButton
									text="Update available"
									size="small"
									css="mt-2"
									onClick={() => {
										if (isElectron)
											return socketActions.visitExternal(
												"https://h2r.graphics/download"
											);

										return window.open(
											"https://h2r.graphics/download",
											"_blank"
										);
									}}
								/>
								<H2RButton
									icon={faExternalLink}
									text="See what's new"
									type="link"
									size="small"
									css="mt-2 whitespace-nowrap"
									onClick={() => {
										if (isElectron)
											return socketActions.visitExternal(
												"https://h2r.graphics/download"
											);

										return window.open(
											"https://h2r.graphics/download",
											"_blank"
										);
									}}
								/>
							</FadeTransition>

							<H2RButton
								text={isPro ? "Pro licence" : "Unlock Pro features"}
								size="small"
								colour={isPro ? "dark" : "main"}
								css="mt-4"
								onClick={() => {
									if (isElectron)
										return socketActions.openSecondWindow("licence");

									return window.open("/licence", "_blank");
								}}
							/>

							<div>
								<div className="flex justify-between w-full">
									<p>Network</p>
									<button
										className="underline text-xs text-gray-500 focus:outline-none focus:ring rounded"
										onClick={() => {
											socketActions.updateIpAddresses();
										}}
									>
										Refresh
									</button>
								</div>
								<H2RSelect
									selected={getSelectedIpAddress()}
									options={[
										...store.ip_addresses?.map((ip) => {
											return {
												label: `${ip} - ${
													ip === "127.0.0.1" ? "This computer" : "Your network"
												}`,
												value: ip,
											};
										}),
									]}
									css="mt-1"
									onChange={(change) => {
										let substring = change.substring(
											0,
											change.lastIndexOf(".")
										);
										socketActions.updateSettings({
											app_ip_address: substring,
										});
									}}
								/>
							</div>

							<FadeTransition show={syncing}>
								<div className="w-36 mt-10 flex flex-col space-y-2">
									<span className="text-sm uppercase animate-pulse">
										Syncing...
									</span>
									{/* <span className="h-1 w-full block bg-gray-600 rounded animate-pulse"></span> */}
								</div>
							</FadeTransition>
						</div>
					) : (
						<SettingsLeft />
					)}

					<div className="flex flex-col justify-center space-y-3">
						<H2RButton
							text="Help docs"
							size="small"
							colour="light"
							icon={faExternalLink}
							onClick={() => {
								if (isElectron)
									return socketActions.visitExternal(
										"https://h2r.li/graphics-v2-docs"
									);

								return window.open("https://h2r.li/graphics-v2-docs", "_blank");
							}}
						/>
						<H2RButton
							text="Report issue"
							size="small"
							colour="light"
							icon={faExternalLink}
							onClick={() => {
								if (isElectron)
									return socketActions.visitExternal("https://h2r.li/issue");

								return window.open("https://h2r.li/issue", "_blank");
							}}
						/>
						<H2RButton
							text={!showSettings ? "Settings" : "Close"}
							colour="dark"
							size="small"
							icon={!showSettings ? faCog : faCheck}
							onClick={() => {
								setShowSettings(!showSettings);
							}}
						/>
					</div>
				</section>
			</div>
			{/* RIGHT */}
			{showSettings ? (
				<SettingsRight />
			) : (
				<section className="px-10 py-8 w-9/12 flex flex-col justify-between bg-gray-800">
					{store?.settings?.selectedProject !== "" &&
					store?.settings?.selectedProject !== undefined ? (
						<>
							<div className="flex flex-col space-y-3">
								<div className="flex justify-between space-x-2">
									<ProjectName
										name={store?.projects?.[selectedProject]?.name}
										selectedProject={selectedProject}
									/>
									{!store?.projects?.[selectedProject]?.lastSynced && (
										<H2RButton
											css="w-min"
											size="small"
											icon={faUpload}
											text="Cloud Sync"
											colour="dark"
											disabled={!isPro || syncing}
											onClick={() => {
												// mainActions.setSyncing(true);
												socketActions.syncInit(selectedProject);
											}}
										/>
									)}
									{store?.projects?.[selectedProject]?.lastSynced && (
										<div className="flex flex-col items-end space-y-1">
											<div className="flex space-x-1">
												<H2RButton
													disabled={!isPro || syncing}
													css="w-min"
													size="small"
													icon={faUpload}
													text=""
													colour="dark"
													onClick={() => {
														// mainActions.setSyncing(true);
														socketActions.syncInit(selectedProject);
													}}
												/>
												<H2RButton
													disabled={!isPro || syncing}
													css="w-min"
													size="small"
													icon={faDownload}
													text=""
													colour="dark"
													onClick={() => {
														// mainActions.setSyncing(true);
														socketActions.syncDown(selectedProject);
													}}
												/>
											</div>

											<span className="text-xs text-gray-500 text-right">
												Version:{" "}
												{moment(
													store?.projects?.[selectedProject]?.lastSynced
												).format("Do MMM, HH:mm:ss")}
											</span>
										</div>
									)}
									{/* <H2RButton
										css="w-min"
										text="Sync UP"
										onClick={() => {
											socketActions.syncUp(selectedProject);
										}}
									/>
									<H2RButton
										css="w-min"
										text="Sync DOWN"
										onClick={() => {
											socketActions.syncDown(selectedProject);
										}}
									/> */}
								</div>
								<H2RHeading type="h4">Control</H2RHeading>
								{/* <p className="text-xs text-gray-500">Add, configure and show/hide graphics</p> */}
								<div className="flex">
									{controls.map((control) => {
										return (
											<div
												className={`rounded-t ${
													controlTab.value === control.value
														? "bg-gray-900"
														: "dark"
												}`}
											>
												<H2RButton
													text={control.name}
													size="small"
													colour={
														controlTab.value === control.value ? "main" : "dark"
													}
													type="link"
													css="rounded-b-none"
													onClick={() => {
														return setControlTab(control);
													}}
												/>
											</div>
										);
									})}
								</div>
								<div
									style={{ margin: 0 }}
									className="py-2 px-2 flex items-center bg-gray-900 rounded"
								>
									<H2RInput
										key={`${controlTab.url}_${store.settings.app_ip_address}`}
										disabled={true}
										value={controlUrl}
										colour="light"
										onChange={() => {
											return null;
										}}
									/>

								<Tooltip>
									<TooltipTrigger>
										<H2RButton
											text="Copy"
											icon={faCopy}
											css="w-min ml-2"
											size="small"
											colour="dark"
											onClick={()=>{
												navigator.clipboard.writeText(controlUrl)
												setCopied('control')
												setTimeout(()=>{setCopied(null)}, [3000])
											}}
										/>
									</TooltipTrigger>
									<TooltipContent>
										{copied === 'control' ? 'Copied!' : 'Copy URL to clipboard'}
									</TooltipContent>
								</Tooltip>
									

								<Tooltip>
									<TooltipTrigger>
										<H2RButton
											text="Open"
											css="w-min ml-2"
											icon={faTv}
											size="small"
											onClick={() => {
												if (isElectron)
													return socketActions.openSecondWindow(
														controlTab.value,
														store.settings?.selectedProject
													);

												return window.open(
													`${`http://${getSelectedIpAddress()}:${
														window.location.port
													}/`}${controlTab.url}/${selectedProject}/`,
													"_blank"
												);
											}}
										/>
									</TooltipTrigger>
									<TooltipContent>
										Open a Control window on this computer
									</TooltipContent>
								</Tooltip>
									
								</div>
								<span className="mt-4"></span>
								<H2RHeading type="h4">Output</H2RHeading>
								{/* <p className="text-xs text-gray-500">Display output which contains all rendered graphics</p> */}
								<div className="flex space-x-2">
									{outputs.map((output) => {
										return (
											<div
												className={`rounded-t ${
													outputTab.value === output.value
														? "bg-gray-900"
														: "dark"
												}`}
											>
												<H2RButton
													disabled={!isPro && output.alternateOutput}
													text={output.name}
													size="small"
													colour={
														outputTab.value === output.value ? "main" : "dark"
													}
													type="link"
													css="rounded-b-none uppercase capitalize"
													onClick={() => {
														return setOutputTab(output);
													}}
												/>
											</div>
										);
									})}
									<ProBadge />
								</div>
								<div
									style={{ margin: 0 }}
									className="relative py-2 px-2 flex items-center bg-gray-900 rounded"
								>
									<H2RInput
										key={`${outputTab.url}_${store.settings.app_ip_address}_${outputTab.name}`}
										value={outputUrl}
										colour="light"
										onChange={() => {
											return null;
										}}
										disabled={true}
									/>

								<Tooltip>
									<TooltipTrigger>
										<a
											title='Drag and Drop into OBS'
											className="ml-2 h-7 w-8 px-2 rounded bg-blue-900 hover:bg-blue-700 transition-colors cursor-move flex items-center"
											href={`${outputUrl}?bg=transparent`} draggable={true} onClick={(e)=>e.preventDefault()}
										>
											<img alt="OBS logo" src={OBSLogo} className="w-8 aspect-square pointer-events-none" />
										</a>
									</TooltipTrigger>
									<TooltipContent>
										<p className="font-semibold">Drag and Drop into OBS Studio</p>
										<p className="text-xs">Drag this icon into your OBS scene to add the graphics output to your project.</p>
									</TooltipContent>
								</Tooltip>

								<Tooltip>
									<TooltipTrigger>
										<H2RButton
											text="Copy"
											icon={faCopy}
											css="w-min ml-2"
											size="small"
											colour="dark"
											onClick={()=>{
												navigator.clipboard.writeText(outputUrl)
												setCopied('output')
												setTimeout(()=>{setCopied(null)}, [3000])
											}}
										/>
									</TooltipTrigger>
									<TooltipContent>
										{copied === 'output' ? 'Copied!' : 'Copy URL to clipboard'}
									</TooltipContent>
								</Tooltip>
									

								<Tooltip>
									<TooltipTrigger>
										<H2RButton
											text="Open"
											css="w-min ml-2"
											size="small"
											icon={faTv}
											onClick={() => {
												if (isElectron)
													return socketActions.openSecondWindow(
														outputTab.url,
														`${store.settings?.selectedProject}${
															outputTab.alternateOutput
																? `/${outputTab.name}/${outputTab.urlQuery ?? ""}`
																: `${outputTab.urlQuery ?? ""}`
														}`,
														outputTab.name
													);

												return window.open(
													`${`http://${getSelectedIpAddress()}:${
														window.location.port
													}/`}${outputTab.url}/${
														store.settings?.selectedProject
													}/${
														outputTab.alternateOutput
															? `${outputTab.name}/`
															: `${outputTab.urlQuery ?? ""}`
													}`,
													"_blank"
												);
											}}
											optionsOpened={()=>{setOpenOnDisplayMenu(true)}}
											optionsClosed={()=>{setOpenOnDisplayMenu(false)}}
											options={[
												{
													label: 'Open on...',
													value: null,
													disabled: true,
												},
												...availableDisplays.map((display, displayIndex) => {
													return {
															label: `Display ${displayIndex + 1} (${
																display.bounds?.width
															} x
															${display.bounds?.height})`,
															value: displayIndex
														}
												})
											]}
											optionClicked={(displayIndex) => {
												console.log('clicked', displayIndex)
												if (isElectron) {
													return socketActions.openSecondWindow(
														outputTab.url,
														`${store.settings?.selectedProject}${
															outputTab.alternateOutput
																? `/${outputTab.name}`
																: `${outputTab.urlQuery ?? ""}`
														}`,
														outputTab.name,
														displayIndex
													);
												}

												return window.open(
													`${`http://${getSelectedIpAddress()}:${
														window.location.port
													}/`}${outputTab.url}/${
														store.settings?.selectedProject
													}/${
														outputTab.alternateOutput
															? `${outputTab.name}/`
															: `${outputTab.urlQuery ?? ""}`
													}`,
													"_blank"
												);
											}}
										/>
									</TooltipTrigger>
									<TooltipContent>
										Open Output in a window on this computer
									</TooltipContent>
								</Tooltip>
									
									{/* <H2RButton
										text="NDI"
										css="w-min ml-2"
										size="small"
										icon={faTv}
										onClick={() => {
											if (isElectron)
												return socketActions.openSecondWindow(
													'ndi',
													`${store.settings?.selectedProject}${
														outputTab.alternateOutput
															? `/${outputTab.name}/${outputTab.urlQuery ?? ""}`
															: `${outputTab.urlQuery ?? ""}`
													}`,
													outputTab.name
												);
										}}
									/> */}
									{/* {openOnDisplayMenu && (
										<div className="p-2 absolute right-0 top-10 bg-gray-900 rounded">
											{availableDisplays.length === 0 ? (
												<span className="text-sm  text-gray-500">
													Checking displays...
												</span>
											) : (
												<span className="flex flex-col space-y-2 shadow-md">
													{availableDisplays.map((display, displayIndex) => {
														return (
															<H2RButton
																key={`display_${displayIndex}`}
																size="small"
																colour="dark"
																text={`Display ${displayIndex + 1} (${
																	display.bounds?.width
																} x
																${display.bounds?.height})`}
																onClick={() => {
																	setOpenOnDisplayMenu(false);
																	return socketActions.openSecondWindow(
																		outputTab.url,
																		`${store.settings?.selectedProject}${
																			outputTab.alternateOutput
																				? `/${outputTab.name}`
																				: `${outputTab.urlQuery ?? ""}`
																		}`,
																		outputTab.name,
																		displayIndex
																	);
																}}
															/>
														);
													})}

													<H2RButton
														size="small"
														colour="dark"
														text={`System browser`}
														onClick={() => {
															setOpenOnDisplayMenu(false);
															socketActions.visitExternal(
																`http://${getSelectedIpAddress()}/${
																	outputTab.url
																}/${store.settings?.selectedProject}${
																	outputTab.alternateOutput
																		? `/${outputTab.name}`
																		: `${outputTab.urlQuery ?? ""}`
																}`
															);
														}}
													/>
												</span>
											)}
										</div>
									)} */}
								</div>
								<H2RHeading type="h4">Data source</H2RHeading>
								<div className="flex flex-col text-gray-400 space-y-2">
									{Object.entries(dataSources).map(([id, source]) => {
										if (source.addedToProjects?.includes(selectedProject))
											return (
												<>
													<div className="px-1 py-1 flex flex-col justify-center items-center border border-gray-600 rounded">
														<div className="flex flex-row items-center">
															<span
																className={`h-4 w-4 rounded-full 
																			${source.status === "error" && "bg-red-500"}
																			${source.status === "connected" && "bg-green-400 animate-pulse"}
																			${source.status === "ready" && "bg-gray-500"}
																		
																					`}
															></span>
															<span className="pl-2 text-sm">
																<span className="font-semibold">
																	{source.details?.name}
																</span>{" "}
																• <span className="select-text">{id}</span>
															</span>
															{source.status === "ready" && (
																<H2RButton
																	text="Connect"
																	label="YouTube video ID"
																	type="link"
																	size="small"
																	colour="light"
																	onClick={() => {
																		return cuesApi.connectToDataSource(
																			id,
																			source.provider
																		);
																	}}
																/>
															)}
															<H2RButton
																colour="light"
																icon={faClose}
																onClick={() => {
																	cuesApi.removeDataSource(id);
																}}
																size="small"
															/>
														</div>
														{source.details?.type === "http_listener" && (
															<span className="text-xs select-text">
																<OpenExternal
																	buttonText={"Learn how to send JSON"}
																	isElectronLink={`https://docs.h2r.graphics/control/data-source/http-listener`}
																	isNotElectronLink={`https://docs.h2r.graphics/control/data-source/http-listener`}
																/>{" "}
																to{" "}
																<span>
																	http://{getSelectedIpAddress()}/data/
																	{id}/
																</span>
															</span>
														)}
													</div>
												</>
											);
										else return null;
									})}
									<AddDataSource />
								</div>
								<span className="pt-2 border-b border-gray-700 w-full block"></span>
								<div className="pt-2">
									<H2RHeading type="h4">Guides</H2RHeading>
									<p className="text-xs text-gray-500">How to get the most out of H2R Graphics</p>
									<div className="mt-2 flex flex-wrap gap-1">
										<OpenExternal
											buttonText={'OBS Studio guide'}
											className="p-2 bg-gray-900 rounded text-white font-bold"
											isElectronLink={'https://h2r.graphics/docs/how-to-use-with/obs/'}
											isNotElectronLink={'https://h2r.graphics/docs/how-to-use-with/obs/'}
										/>
										<OpenExternal
											buttonText={'vMix guide'}
											className="p-2 bg-gray-900 rounded text-white font-bold"
											isElectronLink={'https://h2r.graphics/docs/how-to-use-with/vmix/'}
											isNotElectronLink={'https://h2r.graphics/docs/how-to-use-with/vmix/'}
										/>
										<OpenExternal
											buttonText={'Blackmagic ATEM guide'}
											className="p-2 bg-gray-900 rounded text-white font-bold"
											isElectronLink={'https://h2r.graphics/docs/how-to-use-with/atem/'}
											isNotElectronLink={'https://h2r.graphics/docs/how-to-use-with/atem/'}
										/>
										
									</div> 
								</div>
							</div>
							<div className="relative flex justify-between">
								<H2RButton
									text={"Close project"}
									type="link"
									colour="light"
									size="small"
									css=""
									icon={faArrowLeft}
									onClick={() => {
										setReallyDelete(false);
										return socketActions.updateSettings({
											selectedProject: "",
										});
									}}
								/>

								<H2RButton
									text={"Options"}
									type="link"
									colour="light"
									size="small"
									css=""
									icon={faEllipsis}
									onClick={() => {
										return setMoreProjectOptionsMenu(!moreProjectOptionsMenu);
									}}
								/>

								{moreProjectOptionsMenu && (
									<div className="p-2 absolute right-0 bottom-10 bg-gray-900 rounded">
										<span className="flex flex-col space-y-2 shadow-md">
											<H2RButton
												text={`Duplicate ${!isPro ? "(Pro)" : ""}`}
												colour="dark"
												size="small"
												icon={faCopy}
												disabled={!isPro}
												css=""
												onClick={() => {
													setMoreProjectOptionsMenu(false);
													return socketActions.duplicateProject(
														selectedProject
													);
												}}
											/>
											{!reallyDelete ? (
												<H2RButton
													text={"Delete"}
													colour="dark"
													icon={faTrash}
													size="small"
													css="hover:bg-red-500"
													onClick={() => {
														setReallyDelete(true);
													}}
												/>
											) : (
												<div className="flex space-x-2">
													<H2RButton
														text={"Yes, delete forever"}
														colour="light"
														size="small"
														css="bg-red-500"
														onClick={() => {
															setReallyDelete(false);
															setMoreProjectOptionsMenu(false);
															return socketActions.deleteProject(
																selectedProject
															);
														}}
													/>
													<H2RButton
														text={"Cancel"}
														colour="light"
														size="small"
														css=""
														onClick={() => {
															setReallyDelete(false);
														}}
													/>
												</div>
											)}
										</span>
									</div>
								)}
							</div>
						</>
					) : (
						<>
							<div>
								<H2RHeading type="h2">Open project</H2RHeading>
								<div className="mt-5 mb-1 p-1 flex flex-col space-y-3 overflow-auto">
									{Object.entries(store?.projects || [])
										.reverse()
										.map(([id, p], i) => {
											if (!isPro && i > 0) return null;
											return (
												<button
													className=" px-4 py-2 flex justify-between items-center bg-gray-700  hover:bg-gray-900 rounded transition-colors duration-300 focus:outline-none focus:ring focus:ring-main-500"
													key={i}
													onClick={() => {
														return socketActions.updateSettings({
															selectedProject: id,
														});
													}}
												>
													<span className="text-main-400 text-lg">
														{p?.name || "Graphics project"}
													</span>
													{/* <H2RButton
														text={p?.name || ""}
														type="link"
														size="small"
														css="underline"
														onClick={() => {
															return socketActions.updateSettings({
																selectedProject: id,
															});
														}}
													/> */}
													<span className="flex space-x-2">
														{p.lastSynced && (
															<span className="text-xs uppercase font-semibold text-gray-500">
																<svg
																	className="w-4 h-4"
																	fill="none"
																	xmlns="http://www.w3.org/2000/svg"
																>
																	<path
																		d="M1.75 8.75a2.333 2.333 0 0 0 2.333 2.333h5.25a2.916 2.916 0 1 0-.058-5.832A2.918 2.918 0 1 0 3.57 6.473 2.334 2.334 0 0 0 1.75 8.75v0Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																</svg>
															</span>
														)}
														<span className="text-xs uppercase font-semibold text-gray-500">
															{id}
														</span>
														<span className="text-xs uppercase font-semibold text-gray-400">
															→
														</span>
													</span>
												</button>
											);
										})}
								</div>
							</div>
							<div>
								<div className="flex justify-between mt-4 w-full text-gray-500">

									<div className="flex space-x-2">
										{addProjectFromCloud ? (
											<div className="flex items-center space-x-2">
												<span className="whitespace-pre">Project ID:</span>
												<H2RInput
													value={addProjectFromCloudID}
													size="small"
													colour="light"
													placeholder="E.g. ABCD"
													onChange={(text) => {
														setAddProjectFromCloudID(text);
													}}
												/>
												<H2RButton
													text="Add"
													size="small"
													icon={faDownload}
													disabled={syncing}
													onClick={() => {
														mainActions.setSyncing(true);
														socketActions.syncDown(addProjectFromCloudID);
														setAddProjectFromCloudID("");
														setAddProjectFromCloud(false);
													}}
												/>
												<H2RButton
													text=""
													size="small"
													colour="dark"
													icon={faClose}
													onClick={() => {
														setAddProjectFromCloud(false);
														setAddProjectFromCloudID("");
													}}
												/>
											</div>
										) : (
											<>
												<H2RButton
													disabled={
														isPro
															? false
															: store.projects &&
																Object.entries(store?.projects).length >= 1
													}
													text="Create new project"
													size="small"
													icon={faPlus}
													css="ml-4"
													onClick={() => {
														cuesApi.addProject();
													}}
												/>
												<H2RButton
													disabled={!isPro || syncing}
													text="Sync from cloud"
													size="small"
													icon={faDownload}
													colour="dark"
													css="ml-4"
													onClick={() => {
														setAddProjectFromCloud(true);
													}}
												/>
												<ProBadge />
											</>
										)}
									</div>
								</div>
							</div>
						</>
					)}
				</section>
			)}
		</div>
	);
}

export default App;
